import { useEffect, useRef, useState, type RefObject } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useDesktopInformation } from "./desktopInformation";
import "./desktopVisualPolish.css";

export function desktopTableCopy(language: string) {
  const copy = {
    tr: { hint: "Diğer sütunlar için yatay kaydırın", previous: "Önceki sütunlar", next: "Sonraki sütunlar", fullscreen: "Tam Ekran", search: "Kod veya değer ara..." },
    en: { hint: "Scroll horizontally for more columns", previous: "Previous columns", next: "Next columns", fullscreen: "Full Screen", search: "Search code or value..." },
    ru: { hint: "Прокрутите по горизонтали, чтобы увидеть остальные столбцы", previous: "Предыдущие столбцы", next: "Следующие столбцы", fullscreen: "Полный экран", search: "Поиск по коду или значению..." },
    uz: { hint: "Boshqa ustunlar uchun yon tomonga aylantiring", previous: "Oldingi ustunlar", next: "Keyingi ustunlar", fullscreen: "To‘liq ekran", search: "Kod yoki qiymatni qidiring..." },
  };
  return copy[language as keyof typeof copy] ?? copy.tr;
}

export function DesktopShowroomLabel({ label, sku }: { label: string; sku: boolean }) {
  if (!sku) return <span className="showroom-grid__label">{label}</span>;
  const [code, ...parts] = label.split(" · ");
  let description = parts.join(" · ");
  if (description.startsWith(code + " ")) description = description.slice(code.length).trim();
  return <span className="showroom-grid__label showroom-grid__label--sku"><strong>{code}</strong>{description ? <small>{description}</small> : null}</span>;
}

/** Presentation only: moves the existing scroll viewport; no data or export changes. */
export default function DesktopTableNavigation({ target, label, language, native = false }: {
  target: RefObject<HTMLDivElement | null>; label: string; language: string; native?: boolean;
}) {
  const desktop = useDesktopInformation();
  const scroller = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState({ overflow: false, start: true, end: true });
  const copy = desktopTableCopy(language);
  useEffect(() => {
    if (!desktop || !target.current) return;
    let frame = 0;
    const update = () => {
      const element = scroller.current;
      if (!element) return;
      const maximum = element.scrollWidth - element.clientWidth;
      const next = { overflow: element.clientWidth > 0 && maximum > 2, start: element.scrollLeft < 2, end: element.scrollLeft >= maximum - 2 };
      setPosition(previous => previous.overflow === next.overflow && previous.start === next.start && previous.end === next.end ? previous : next);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
    const resize = new ResizeObserver(schedule);
    const bind = () => {
      const element = native ? target.current : target.current?.querySelector<HTMLElement>(".ag-body-horizontal-scroll-viewport");
      if (!element || scroller.current === element) return;
      scroller.current?.removeEventListener("scroll", schedule);
      scroller.current = element;
      element.addEventListener("scroll", schedule, { passive: true });
      resize.observe(element);
      if (element.firstElementChild) resize.observe(element.firstElementChild);
      schedule();
    };
    const pending = new MutationObserver(bind);
    pending.observe(target.current, { childList: true, subtree: true });
    bind();
    return () => { pending.disconnect(); resize.disconnect(); cancelAnimationFrame(frame); scroller.current?.removeEventListener("scroll", schedule); scroller.current = null; };
  }, [desktop, native, target]);
  if (!desktop || !position.overflow) return null;
  const move = (direction: number) => {
    const element = scroller.current;
    if (element) element.scrollBy({ left: direction * Math.max(180, element.clientWidth * .75), behavior: "auto" });
  };
  return <div className="desktop-table-navigation" role="group" aria-label={`${label} · ${copy.hint}`}>
    <span>{copy.hint}</span>
    <div>
      <button type="button" disabled={position.start} aria-label={copy.previous} title={copy.previous} onClick={() => move(-1)}><ChevronLeft aria-hidden="true" size={16} /></button>
      <button type="button" disabled={position.end} aria-label={copy.next} title={copy.next} onClick={() => move(1)}><ChevronRight aria-hidden="true" size={16} /></button>
    </div>
  </div>;
}
